import { ApiError, errorMessage } from "@api/errors";
import type { SeasonDeleted } from "@api/generated/model";
import { useWorkspaceSeasons } from "@api/generated/workspace/workspace";
import { useCreateSeason, useDeleteSeason, useSeason } from "@app/workspace";
import { ResultBanner } from "@features/system/undo";
import { Button, Group, Kbd, Modal, Select, Stack, Text } from "@mantine/core";
import { useTableText } from "@ui/audit";
import { formatNumber } from "@ui/format";
import { YearInput } from "@ui/YearInput";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

export const SHORTCUTS: { group: string; items: [string, string][] }[] = [
  {
    group: "everywhere",
    items: [
      ["quickSearch", "Ctrl K"],
      ["scanPlotCode", "#"],
      ["switchYear", "Alt Y"],
      ["commandList", "?"],
    ],
  },
  {
    group: "inGrid",
    items: [
      ["editSave", "Enter"],
      ["pasteBlock", "Ctrl V"],
      ["fillDown", "Ctrl D"],
      ["undo", "Ctrl Z"],
      ["cellHistory", "Alt H"],
    ],
  },
  {
    group: "inPickLists",
    items: [
      ["mark", "Space"],
      ["markRange", "Shift ↓"],
      ["addToTarget", "F8"],
      ["removeFromTarget", "Del"],
    ],
  },
];

/** A key as printed on the keyboard: named keys in the UI language, the rest as they are. */
export const keyCap = (key: string, t: (key: string) => string) => (key === "Space" ? t("shortcuts.space") : key);

export function ShortcutList() {
  const { t } = useTranslation();
  return (
    <Stack gap={4}>
      {SHORTCUTS.map((g, gi) => (
        <div key={g.group}>
          <span className="sec" style={{ display: "block", marginTop: gi ? 10 : 4 }}>
            {t(`shortcuts.${g.group}`)}
          </span>
          {g.items.map(([label, key]) => (
            <div
              key={label}
              className="hstack"
              style={{ gap: 10, padding: "6px 0", borderBottom: "1px solid var(--b-row)", fontSize: "var(--b-text-body)" }}
            >
              <span className="grow">{t(`shortcuts.${label}`)}</span>
              <Kbd>{keyCap(key, t)}</Kbd>
            </div>
          ))}
        </div>
      ))}
    </Stack>
  );
}

export function ShortcutsModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <Modal opened={opened} onClose={onClose} title={<b>{t("shortcuts.title")}</b>} size="md">
      <ShortcutList />
    </Modal>
  );
}

/** Rendered only while open, so the proposed year is computed fresh each time. */
export function NewSeasonModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { years, setEv } = useSeason();
  const [ev, setValue] = useState<number | string>(years.length ? Math.max(...years) + 1 : new Date().getFullYear());
  const { create, pending } = useCreateSeason((year) => {
    setEv(year);
    onClose();
  });
  return (
    <Modal opened onClose={onClose} title={<b>{t("shell.openYearTitle")}</b>}>
      <Stack>
        <Text size="sm" c="dimmed">
          {t("shell.openYearDescription")}
        </Text>
        <YearInput label={t("shell.yearField")} value={ev} onChange={setValue} min={1950} w={140} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t("shell.cancel")}
          </Button>
          <Button loading={pending} onClick={() => create(Number(ev))}>
            {t("shell.open")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Remove an empty season opened by mistake (administrators; the legacy table designer's Delete Database, made
 *  safe): only seasons without plots and trials are offered, and the server refuses one that anything still names
 *  (the counts are shown). The result stays in the dialog with its undo (ResultBanner). */
export function DeleteSeasonModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const tableText = useTableText();
  const seasons = useWorkspaceSeasons().data ?? [];
  const empty = seasons.filter((s) => s.plots === 0 && s.trials === 0);
  const [ev, setEv] = useState<string | null>(empty.length === 1 ? String(empty[0]?.ev) : null);
  const [done, setDone] = useState<{ ev: number; result: SeasonDeleted } | null>(null);
  const { remove, pending, error, reset } = useDeleteSeason((result) => setDone({ ev: Number(ev), result }));
  if (done) {
    return (
      <Modal opened onClose={onClose} title={<b>{t("shell.deleteYearTitle")}</b>}>
        <Stack>
          <ResultBanner operation={done.result.operation} after={null}>
            {t("shell.deleteYearDone", { ev: done.ev, newValue: done.result.working_season })}
          </ResultBanner>
          <Group justify="flex-end">
            <Button onClick={onClose}>{t("shell.deleteYearClose")}</Button>
          </Group>
        </Stack>
      </Modal>
    );
  }
  const counts = error instanceof ApiError ? (error.problem as { counts?: Record<string, number> }).counts : undefined;
  return (
    <Modal opened onClose={onClose} title={<b>{t("shell.deleteYearTitle")}</b>}>
      <Stack>
        <Text size="sm" c="dimmed">
          {t("shell.deleteYearDescription")}
        </Text>
        {empty.length ? (
          <Select
            label={t("shell.yearField")}
            w={160}
            data={empty.map((s) => String(s.ev))}
            value={ev}
            onChange={(v) => {
              setEv(v);
              reset();
            }}
            data-autofocus
          />
        ) : (
          <div className="note">{t("shell.deleteYearNone")}</div>
        )}
        {error && (
          <div className="note err">
            {counts ? (
              <>
                {t("shell.deleteYearNotEmpty")}{" "}
                {Object.entries(counts)
                  .map(([table, n]) => `${tableText(table.split(".").pop() ?? table)} (${table}): ${formatNumber(n)}`)
                  .join(", ")}
              </>
            ) : (
              errorMessage(error)
            )}
          </div>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t("shell.cancel")}
          </Button>
          <Button color="red" disabled={!ev} loading={pending} onClick={() => ev && remove(Number(ev))}>
            {t("shell.deleteYearButton")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** A yes/no question before an action that changes data (the action itself stays undoable in the Napló). ``children``
 *  says what exactly is affected (a scope choice, the first rows); a ``danger`` question starts on "Mégse", so an
 *  Enter pressed out of habit (a scanner, a keyboard run) does not confirm it. */
export function ConfirmModal({
  title,
  text,
  confirm,
  danger,
  loading,
  disabled,
  onConfirm,
  onClose,
  children,
}: {
  title: string;
  text?: ReactNode;
  confirm: string;
  danger?: boolean;
  loading?: boolean;
  disabled?: boolean;
  onConfirm: () => void;
  onClose: () => void;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <Modal opened onClose={onClose} title={<b>{title}</b>}>
      <Stack>
        {text && <Text size="sm">{text}</Text>}
        {children}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} data-autofocus={danger || undefined}>
            {t("shell.cancel")}
          </Button>
          <Button color={danger ? "red" : undefined} loading={loading} disabled={disabled} onClick={onConfirm}>
            {confirm}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
